import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { toast } from 'react-toastify';

import { requestInvoice } from 'services/balance';
import { useSelector } from 'store';
import { selectActiveCompany } from 'store/companiesReducer';
import { selectUser } from 'store/usersReducer';
import YM from 'utils/metrics';
import { validateValue } from 'utils/validation';
import Button from 'components/Button';
import Input from 'components/Input';
import Radio from 'components/Input/Radio';
import Modal from '../';
import classes from './modalBalanceRefill.module.scss';
import { IModalBalanceRefill } from './types';

const ModalBalanceRefill: React.FC<IModalBalanceRefill> = ({
	className,
	isOpen,
	onClose,
	value,
}) => {
	const user = useSelector(selectUser);
	const activeCompany = useSelector(selectActiveCompany);
	const [balance, setBalance] = useState(value || '');
	const [paymentOption, setPaymentOption] = useState('card');
	const [isInvoiceSuccessModal, setIsInvoiceSuccessModal] = useState(false);
	const [error, setError] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const paymentOptions = [
		{
			value: 'card',
			label: 'Оплатить картой',
			className: classes.paymentOption,
		},
		{
			value: 'invoice',
			label: 'Получить счет на оплату',
			className: classes.paymentOption,
		},
	];

	const resetFormData = () => {
		setBalance(value || '');
		setError('');
		setPaymentOption('card');
		setIsInvoiceSuccessModal(false);
	};

	useEffect(() => {
		if (!isOpen) {
			resetFormData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	useEffect(() => {
		setBalance(value || '');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	const validateBalance = (value: string): boolean => {
		const newError = validateValue({
			type: 'balance',
			value,
			isRequired: true,
		});
		setError(newError);
		const isValid = !newError;
		return isValid;
	};

	const handleChangeBalance = (value: string): void => {
		setBalance(value);
		validateBalance(value);
	};

	const handleChangePaymentOption = (value: string): void => {
		setPaymentOption(value);
	};

	const payCard = () => {
		const widget = new (window as any).cp.CloudPayments();
		widget.pay(
			'auth',
			{
				publicId: 'pk_bd97cdc984a2ccd208dd5035a86b1',
				description: 'Пополнение баланса личного кабинета',
				amount: Number(balance),
				currency: 'RUB',
				accountId: String(activeCompany?.RtkLabUserId || ''),
				invoiceId: String(Date.now()),
				email: user?.email || '',
				skin: 'mini',
			},
			{
				onSuccess: function () {
					toast.success(
						'Спасибо! Ваш платеж в обработке. Обычно пополнение счета занимает до 30 минут. Если у вас возникли сложности или вопросы по поводу зачисления средств, обратитесь по телефону, указанному в чеке. Чек был отправлен на ваш email'
					);
				},
				onFail: function (reason: string) {
					if (reason === 'User has cancelled') return;
					toast.error(
						`Не удалось пополнить баланс.${
							reason ? ` ${reason}` : ''
						}`
					);
				},
			}
		);
		onClose();
	};

	const payInvoice = async () => {
		try {
			await requestInvoice({
				tin: activeCompany?.tin || '',
				amount: balance,
			});
			setIsInvoiceSuccessModal(true);
		} catch (e) {
			toast.error('Не удалось пополнить баланс');
		}
	};

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>
	): Promise<void> => {
		e.preventDefault();
		if (isLoading) return;

		const isValid = validateBalance(balance);
		if (!isValid) return;

		setIsLoading(true);
		try {
			paymentOption === 'card' ? payCard() : await payInvoice();
			YM.onFormSubmit();
		} catch (e) {
			toast.error('Не удалось пополнить баланс');
		}
		setIsLoading(false);
	};

	const handleCloseInvoiceSuccess = () => {
		setIsInvoiceSuccessModal(false);
		onClose();
	};

	const form = (
		<form className={classes.form} onSubmit={handleSubmit}>
			<Input
				className={classes.input}
				value={balance}
				onChange={handleChangeBalance}
				type="number"
				placeholder="Введите сумму, руб."
				error={error}
			/>
			<Radio
				className={classes.paymentOptions}
				value={paymentOption}
				options={paymentOptions}
				onChange={handleChangePaymentOption}
			/>
			<div className={classes.info}>
				Пополняя баланс, Вы соглашаетесь с условиями{' '}
				<Link
					className={classes.infoLink}
					target="_blank"
					rel="noreferrer"
					to="/public-offer"
				>
					Публичной оферты
				</Link>
			</div>
			<div className={classes.buttons}>
				<Button
					className={classes.buttonSubmit}
					text="Пополнить"
					type="submit"
					isLoading={isLoading}
				/>
				<Button
					className={classes.buttonCancel}
					text="Отмена"
					styleType="secondary"
					onClick={onClose}
				/>
			</div>
		</form>
	);

	const invoiceSuccessContent = (
		<>
			<div className={classes.invoiceSuccessContent}>
				Мы отправим счет на оплату на вашу электронную почту в течение
				рабочего дня. Если счет был создан в нерабочее время после 19:00
				(GMT+3), в выходной или праздничный день, то счет придет в
				первый рабочий день
			</div>
			<Button
				className={classes.button}
				text="Хорошо"
				onClick={handleCloseInvoiceSuccess}
			/>
		</>
	);

	return (
		<Modal
			className={cn(classes.modalBalanceRefill, className)}
			isOpen={isOpen}
			onClose={onClose}
		>
			<div className={classes.title}>
				{isInvoiceSuccessModal
					? 'Счет на оплату создан'
					: 'Сумма пополнение баланса'}
			</div>
			{isInvoiceSuccessModal ? invoiceSuccessContent : form}
		</Modal>
	);
};

export default ModalBalanceRefill;
